// Star sprites for the sky canvas, baked once per tint.
//
// A star on a real sensor is not a soft blob: it is a point spread
// function, a hard bright core inside a glare that falls off with the
// square of the distance, and on the brightest stars thin diffraction
// spikes that keep the same inverse-square law along their length. The
// sprites below bake that profile so the draw loop only scales and tints.
// Faint stars never get a sprite at all; they stay sub-pixel points, which
// is what keeps a dense field from turning into porridge.

// Blackbody-ish tints from hot to cool, in linear-ish RGB. Most of a night
// sky reads white or blue-white; warm stars are the minority.
export const STAR_TINTS = [
  { key: 'blue', rgb: [0.80, 0.88, 1.00], weight: 0.22 },
  { key: 'white', rgb: [0.96, 0.97, 1.00], weight: 0.38 },
  { key: 'cream', rgb: [1.00, 0.96, 0.88], weight: 0.22 },
  { key: 'gold', rgb: [1.00, 0.87, 0.68], weight: 0.12 },
  { key: 'amber', rgb: [1.00, 0.76, 0.55], weight: 0.06 },
];

export function pickTint(u) {
  let acc = 0;
  for (let i = 0; i < STAR_TINTS.length; i++) {
    acc += STAR_TINTS[i].weight;
    if (u < acc) return i;
  }
  return STAR_TINTS.length - 1;
}

const smooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

// Intensity of the point spread function at a pixel offset from the
// centre, all lengths in sprite pixels. `spikes` adds the four-arm
// diffraction cross with a fainter diagonal pair.
function psf(dx, dy, radius, spikes) {
  const r = Math.hypot(dx, dy);
  const edge = 1 - smooth(radius * 0.72, radius, r);
  const core = Math.exp(-(r * r) / (2 * 1.35 * 1.35));
  const glare = 0.32 / (1 + (r / 3.2) ** 2);
  let i = core + glare * edge;
  if (spikes) {
    const arm = (along, across, gain, width, reach) =>
      gain * Math.exp(-(across * across) / (2 * width * width)) / (1 + (along / reach) ** 2) * edge;
    i += arm(Math.abs(dx), dy, 0.55, 0.85, 5);
    i += arm(Math.abs(dy), dx, 0.55, 0.85, 5);
    const d1 = Math.abs(dx + dy) / Math.SQRT2;
    const d2 = Math.abs(dx - dy) / Math.SQRT2;
    i += arm(d1, d2, 0.14, 0.8, 4);
    i += arm(d2, d1, 0.14, 0.8, 4);
  }
  return Math.min(1, i);
}

function bake(size, tint, spikes) {
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  const img = ctx.createImageData(size, size);
  const data = img.data;
  const half = size / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = psf(x + 0.5 - half, y + 0.5 - half, half, spikes);
      // The core burns to white; the glare and spikes carry the tint.
      const white = Math.min(1, i * i * 1.4);
      const o = (y * size + x) * 4;
      data[o] = Math.round(255 * (tint[0] + (1 - tint[0]) * white));
      data[o + 1] = Math.round(255 * (tint[1] + (1 - tint[1]) * white));
      data[o + 2] = Math.round(255 * (tint[2] + (1 - tint[2]) * white));
      data[o + 3] = Math.round(255 * i);
    }
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

// Returns one { glare, spike } pair per tint, or null without a 2D context
// (tests). `glare` is 48px across, `spike` 112px; both are drawn scaled.
export const GLARE_SIZE = 48;
export const SPIKE_SIZE = 112;
export function makeStarSprites() {
  if (typeof document === 'undefined') return null;
  const sprites = STAR_TINTS.map((t) => ({ glare: bake(GLARE_SIZE, t.rgb, false), spike: bake(SPIKE_SIZE, t.rgb, true) }));
  return sprites.every((s) => s.glare && s.spike) ? sprites : null;
}
